import { Select as BSelect } from '@base-ui/react/select'
import { useState } from 'react'
import { Checkbox } from './checkbox'

// use leading null + label for placeholder
export type SelectItem<Val> = { value: Val | null; label: string; disabled?: boolean }

export type SingleItemProps<Val> = {
  value?: Val | null
  defaultValue?: Val
  onChange?: (val: Val | null) => void
  isMulti?: false | null // must not be true
}

export type MultiItemProps<Val> = {
  value?: Val[]
  defaultValue?: Val[]
  onChange?: (val: Val[]) => void
  isMulti: true // must be true
}
export type SelectProps<Val> = (SingleItemProps<Val> | MultiItemProps<Val>) & {
  items: SelectItem<Val>[]
  disabled?: boolean
  id?: string
  name?: string
  required?: boolean
  onOpenChange?: (open: boolean) => void
}

export function Select<Val>(props: SelectProps<Val>) {
  const { items, onChange, isMulti, id, onOpenChange, ...rest } = props
  const [hasOpened, setHasOpened] = useState(false)
  const findItem = (value: Val) => items.find((i) => i.value === value)

  const handleOpenChange = (open: boolean) => {
    if (open) setHasOpened(true)
    onOpenChange?.(open)
  }

  return (
    <BSelect.Root
      items={items}
      // @ts-expect-error the arguments to the callback can vary based on isMulti and TS can't follow that here
      onValueChange={onChange}
      multiple={isMulti ?? false}
      onOpenChange={handleOpenChange}
      {...rest}
    >
      <BSelect.Trigger
        id={id}
        className={
          'bg-white border border-gray-stroke focus:border-lavender h-10 w-full rounded-lg px-4 focus:ring-0 flex flex-row items-center justify-between text-sm data-disabled:opacity-40'
        }
      >
        <BSelect.Value>
          {isMulti
            ? (selected: Val[]) => `${selected.length > 0 ? selected.length : 'None'} Selected`
            : (selected: Val) => findItem(selected)?.label}
        </BSelect.Value>
        <BSelect.Icon>
          <i className="fa-solid fa-caret-down text-charcoal-50"></i>
        </BSelect.Icon>
      </BSelect.Trigger>
      <BSelect.Positioner align="start" className="outline-none z-10 select-none">
        <BSelect.Popup className="box-border border rounded-lg border-gray-stroke bg-white flex flex-col w-[var(--anchor-width)] origin-[var(--transform-origin)] overflow-y-auto max-h-[var(--available-height)]">
          {hasOpened &&
            items.map(({ label, value, disabled }) => (
              <BSelect.Item
                key={String(value)}
                value={value}
                disabled={disabled}
                className="px-4 py-2 w-full text-left cursor-default scroll-m-0.5 select-none outline-none data-disabled:opacity-40 data-disabled:data-highlighted:bg-white data-selected:text-forest data-highlighted:bg-sand"
                nativeButton
                render={(props, state) => (
                  <button type="button" {...props}>
                    <div className="flex items-center gap-2">
                      {isMulti && (
                        <Checkbox
                          checked={state.selected}
                          disabled={disabled}
                          tabIndex={-1}
                          aria-hidden="true"
                          className="pointer-events-none"
                        />
                      )}
                      <BSelect.ItemText className="text-sm">{label}</BSelect.ItemText>
                    </div>
                  </button>
                )}
              />
            ))}
        </BSelect.Popup>
      </BSelect.Positioner>
    </BSelect.Root>
  )
}
